import { useAuth } from '../../api/auth'
import { ApiError } from '../../api/client'
import { useMarketplaceAccounts, useCheckMarketplaceAccount, type MarketplaceAccountStatus } from '../../api/marketplace-accounts'
import { PageHeader } from '@/components/PageHeader'
import { DataTable, type Column } from '@/components/DataTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const STATES = {
  nicht_eingerichtet: { text: 'nicht eingerichtet', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  fehler: { text: 'Fehler', color: 'bg-red-100 text-red-800 border-red-200' },
  warnung: { text: 'Warnung', color: 'bg-[#FFB300]/20 text-[#8a6100] border-[#FFB300]/50' },
  aktiv: { text: 'aktiv', color: 'bg-[#108474]/10 text-[#108474] border-[#108474]/30' },
  veraltet: { text: 'veraltet', color: 'bg-gray-100 text-[#8a6100] border-[#FFB300]/50' },
  unbekannt: { text: 'unbekannt', color: 'bg-gray-100 text-gray-700 border-gray-200' },
}
const STALE_HINT = 'Letzte Prüfung älter als 90 Minuten'
function displayStatus(a: MarketplaceAccountStatus) {
  return a.stale && a.status === 'aktiv' ? 'veraltet' : a.status ?? 'unbekannt'
}
function date(value: string | null): string {
  return value ? new Date(value).toLocaleString('de-DE') : 'unbekannt'
}
function CheckButton({ account }: { account: MarketplaceAccountStatus }) {
  const check = useCheckMarketplaceAccount()
  const retry = check.error instanceof ApiError && check.error.status === 429 ? check.error.details.retry_after_sec : null
  return <div className="space-y-1">
    <Button variant="outline" size="sm" disabled={check.isPending} onClick={() => check.mutate(account.key)}>
      {check.isPending ? 'Prüfung läuft …' : 'Jetzt prüfen'}
    </Button>
    {check.error && <p role="status" className="text-xs text-amber-800">
      {typeof retry === 'number' ? `Bitte in ${retry} Sekunden erneut prüfen.` : 'Prüfung konnte nicht gestartet werden.'}
    </p>}
  </div>
}

const GROUPS: { title: string; platforms: MarketplaceAccountStatus['platform'][] }[] = [
  { title: 'eBay', platforms: ['ebay'] },
  { title: 'Amazon', platforms: ['amazon'] },
  { title: 'Weitere Portale', platforms: ['kaufland', 'metro', 'shopify'] },
]
const DEADLINE_HINTS = { ebay_refresh_token: 'eBay Refresh-Token', amazon_client_secret: 'Amazon Client-Secret' }

// Feste Breiten: alle Abschnitte stehen spaltengenau untereinander; Überbreite verteilt der Browser, darunter scrollt die Tabelle.
const columns: Column<MarketplaceAccountStatus>[] = [
  { key: 'company', header: 'Firma', width: 80 },
  { key: 'label', header: 'Konto', width: 130, cell: a => <span className="font-medium">{a.label}</span> },
  { key: 'expected_identity', header: 'Kennung', width: 150, cell: a => <div className="break-all">
    <span>{a.expected_identity}</span>
    {a.identity && a.identity.toLowerCase() !== a.expected_identity.toLowerCase() && <p className="mt-1 text-xs text-red-700">angemeldet als {a.identity}</p>}
  </div> },
  { key: 'status', header: 'Status', width: 110, cell: a => {
    const key = displayStatus(a), state = STATES[key]
    return <div className="flex flex-wrap gap-1">
      <Badge variant="outline" className={state.color} title={key === 'veraltet' ? STALE_HINT : undefined}>{state.text}</Badge>
      {a.stale && key !== 'veraltet' && <Badge variant="outline" className={STATES.veraltet.color} title={STALE_HINT}>veraltet</Badge>}
    </div>
  } },
  { key: 'message', header: 'Meldung', width: 200, cell: a => <div className="whitespace-normal">
    {a.message}
    {a.missing_env.length > 0 && <p className="mt-1 break-all text-xs text-muted-foreground">Fehlt: {a.missing_env.join(', ')}</p>}
  </div> },
  { key: 'checked_at', header: 'Zuletzt geprüft', width: 140, cell: a => date(a.checked_at) },
  { key: 'last_ok_at', header: 'Letzte erfolgreiche Prüfung', width: 140, className: 'whitespace-normal', cell: a => date(a.last_ok_at) },
  { key: 'deadline_at', header: 'Frist', width: 130, cell: a => <span title={a.deadline_kind ? DEADLINE_HINTS[a.deadline_kind] : undefined}>{date(a.deadline_at)}</span> },
  { key: 'actions', header: 'Prüfung', width: 120, cell: a => <CheckButton account={a} /> },
]

export default function MarktplatzKonten() {
  const { isAdmin } = useAuth()
  const { data, isLoading, isError } = useMarketplaceAccounts(isAdmin)
  if (!isAdmin) return <div className="p-6"><p>Keine Berechtigung</p></div>
  return <div className="p-6">
    <PageHeader title="Marktplatzkonten" breadcrumb={[{ label: 'Admin' }, { label: 'Marktplatzkonten' }]} />
    {data && <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
      <Badge variant="outline">Takt {data.takt_enabled ? 'an' : 'aus'}</Badge>
      <span>Backend gestartet um {date(data.backend_started_at)}</span>
    </div>}
    {isError && <p role="alert" className="mb-4 text-red-700">Kontostatus konnte nicht geladen werden.</p>}
    {data && <section aria-label="Kontenübersicht" className="mb-6 flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 px-4 py-3 text-sm">
      <span className="font-medium text-[#108474]">{data.accounts.filter(a => displayStatus(a) === 'aktiv').length} von {data.accounts.length} aktiv</span>
      {(['fehler', 'warnung', 'nicht_eingerichtet', 'veraltet'] as const).map(status => {
        const count = data.accounts.filter(a => displayStatus(a) === status).length
        return count > 0 && <Badge key={status} variant="outline" className={STATES[status].color}>
          {count} {status === 'warnung' && count > 1 ? 'Warnungen' : STATES[status].text}
        </Badge>
      })}
    </section>}
    {GROUPS.map(group => <section key={group.title} className="mb-8 space-y-3" aria-label={group.title}>
      <h2 className="text-lg font-semibold">{group.title}</h2>
      <DataTable data={data?.accounts.filter(a => group.platforms.includes(a.platform)) ?? []} columns={columns} rowKey={a => a.key} isLoading={isLoading} emptyMessage="Keine Konten geladen" tableFixed />
    </section>)}
  </div>
}
